// AI-assisted question-variants curation panel.
//
// Per design handoff:
//   - README.md § 6b (`for-engineering-kb/`)
//   - specs/kb-ai-variants-strings.md
//
// Rendered inline below the variants list in QaPairSubform. Handles all 7
// states from the spec (idle / disabled / loading / loaded / empty / error /
// post-curation). Emits accepted variants back to the parent via `onAccept`
// (one string at a time); the parent appends to `body.question_variants`.
//
// Ephemeral panel: no persisted "AI-generated" flag; once accepted, a variant
// is indistinguishable from a manually-typed one (per §2 of the spec).
// Dismissing the panel discards any unaccepted candidates — re-run
// `Suggest more` to fetch a fresh batch.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { KBEntryId, KBEntryLanguage } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { suggestKBQaPairVariants } from '../api/endpoints.js';
import { AIGlyph } from './AIGlyph.js';
import styles from './KBEditor.module.css';

type Status = 'loading' | 'loaded' | 'empty' | 'error';

interface Candidate {
  key: number;
  text: string;
  editing: boolean;
  editDraft: string;
}

export interface AIVariantsPanelProps {
  entryId: KBEntryId;
  language: KBEntryLanguage;
  /** Canonical answer, shown in the collapsible "Grounded on this answer" accordion. */
  answerText: string;
  /** Persisted `body.question_variants` + accepted-in-session — sent to the LLM as exclusions. */
  excludeVariants: string[];
  /** Called for each accepted candidate (either as-is or edited). Parent appends to `body.question_variants`. */
  onAccept: (text: string) => void;
  /** Called when the panel should close (✕ dismiss). */
  onDismiss: () => void;
}

export function AIVariantsPanel({
  entryId,
  language,
  answerText,
  excludeVariants,
  onAccept,
  onDismiss,
}: AIVariantsPanelProps) {
  const { t } = useI18n();
  const [status, setStatus] = useState<Status>('loading');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [groundingOpen, setGroundingOpen] = useState(false);
  const [batchIdx, setBatchIdx] = useState(0);
  const batchIdxRef = useRef(0);

  // Track the FULL exclusion set locally so successive Suggest-more calls
  // remember accepted-in-panel candidates the parent hasn't necessarily
  // persisted yet.
  const [effectiveExclusions, setEffectiveExclusions] = useState<string[]>(
    () => Array.from(new Set(excludeVariants)),
  );

  const fetchBatch = useCallback(async () => {
    setStatus('loading');
    const result = await suggestKBQaPairVariants(entryId, {
      exclude_variants: effectiveExclusions,
    });
    if (!result.ok) {
      setStatus('error');
      return;
    }
    const items = result.data.variants ?? [];
    if (items.length === 0) {
      setCandidates([]);
      setStatus('empty');
      return;
    }
    batchIdxRef.current += 1;
    setBatchIdx(batchIdxRef.current);
    const idx = batchIdxRef.current;
    setCandidates(
      items.map((text, i) => ({
        key: idx * 100 + i,
        text,
        editing: false,
        editDraft: text,
      })),
    );
    setStatus('loaded');
  }, [entryId, effectiveExclusions]);

  // Fetch on mount only; Suggest more re-triggers fetchBatch via the button.
  useEffect(() => {
    void fetchBatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleEdit(key: number) {
    setCandidates((cs) =>
      cs.map((c) => (c.key === key ? { ...c, editing: true, editDraft: c.text } : c)),
    );
  }
  function updateEditDraft(key: number, value: string) {
    setCandidates((cs) => cs.map((c) => (c.key === key ? { ...c, editDraft: value } : c)));
  }
  function confirmEdit(key: number) {
    const target = candidates.find((c) => c.key === key);
    if (!target) return;
    const trimmed = target.editDraft.trim();
    if (trimmed.length === 0) return;
    acceptOne(key, trimmed);
  }
  function acceptOne(key: number, textOverride?: string) {
    const target = candidates.find((c) => c.key === key);
    if (!target) return;
    const text = (textOverride ?? target.text).trim();
    if (text.length === 0) return;
    onAccept(text);
    setEffectiveExclusions((prev) =>
      prev.includes(text) ? prev : [...prev, text],
    );
    setCandidates((cs) => cs.filter((c) => c.key !== key));
  }
  function rejectOne(key: number) {
    setCandidates((cs) => cs.filter((c) => c.key !== key));
  }
  function acceptAll() {
    const additions = candidates.map((c) => c.text.trim()).filter((tt) => tt.length > 0);
    for (const text of additions) {
      onAccept(text);
    }
    setEffectiveExclusions((prev) => {
      const next = new Set(prev);
      for (const text of additions) next.add(text);
      return Array.from(next);
    });
    setCandidates([]);
  }
  function rejectAll() {
    setCandidates([]);
  }

  return (
    <div className={styles.aiPanel} data-status={status} data-language={language}>
      <div className={styles.aiPanelHeader}>
        <div className={styles.aiPanelTitle}>
          <AIGlyph />
          <span>{t('kb.aiVariantsPanelTitle')}</span>
          {status === 'loaded' && candidates.length > 0 && (
            <span className={styles.aiPanelCount}>{candidates.length}</span>
          )}
        </div>
        <div className={styles.aiPanelHeaderActions}>
          {status === 'loaded' && candidates.length > 0 && (
            <>
              <button
                type="button"
                className={styles.aiPanelTextAction}
                onClick={acceptAll}
              >
                {t('kb.aiVariantsAcceptAll')}
              </button>
              <button
                type="button"
                className={styles.aiPanelTextAction}
                onClick={rejectAll}
              >
                {t('kb.aiVariantsRejectAll')}
              </button>
            </>
          )}
          <button
            type="button"
            className={styles.aiPanelDismiss}
            onClick={onDismiss}
            aria-label={t('kb.aiVariantsDismiss')}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Grounding accordion — visible in all non-loading states. */}
      {status !== 'loading' && (
        <div className={styles.aiPanelGrounding}>
          <button
            type="button"
            className={styles.aiPanelGroundingToggle}
            onClick={() => setGroundingOpen((v) => !v)}
            aria-expanded={groundingOpen}
          >
            {t('kb.aiVariantsGroundingToggle')} {groundingOpen ? '▴' : '▾'}
          </button>
          {groundingOpen && (
            <div className={styles.aiPanelGroundingBody}>{answerText}</div>
          )}
        </div>
      )}

      {status === 'loading' && (
        <div className={styles.aiPanelBodyCenter}>
          <div className={styles.aiSpinner} aria-hidden="true" />
          <div className={styles.aiPanelLoadingTitle}>{t('kb.aiVariantsLoading')}</div>
          <div className={styles.aiPanelLoadingSub}>{t('kb.aiVariantsLoadingSub')}</div>
        </div>
      )}

      {status === 'empty' && (
        <div className={styles.aiPanelBodyCenter}>
          <div className={styles.aiPanelEmpty}>{t('kb.aiVariantsEmpty')}</div>
          <button
            type="button"
            className={styles.aiPanelSuggestMore}
            onClick={() => {
              void fetchBatch();
            }}
          >
            ↻ {t('kb.aiVariantsSuggestMore')}
          </button>
        </div>
      )}

      {status === 'error' && (
        <div className={styles.aiPanelBodyCenter}>
          <div className={styles.aiPanelError}>{t('kb.aiVariantsError')}</div>
          <button
            type="button"
            className={styles.aiPanelSuggestMore}
            onClick={() => {
              void fetchBatch();
            }}
          >
            {t('kb.aiVariantsErrorRetry')}
          </button>
        </div>
      )}

      {status === 'loaded' && candidates.length > 0 && (
        <>
          <ul className={styles.aiPanelList}>
            {candidates.map((c) => (
              <li key={c.key} className={styles.aiPanelRow}>
                <span className={styles.aiPanelRowTag} aria-hidden="true">
                  <AIGlyph /> {t('kb.aiVariantsProvenanceTag')}
                </span>
                {c.editing ? (
                  <>
                    <input
                      type="text"
                      className={styles.singleLineInput}
                      value={c.editDraft}
                      onChange={(e) => updateEditDraft(c.key, e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      className={styles.aiPanelRowConfirm}
                      onClick={() => confirmEdit(c.key)}
                    >
                      {t('kb.aiVariantsRowConfirm')}
                    </button>
                  </>
                ) : (
                  <>
                    <span className={styles.aiPanelRowText}>{c.text}</span>
                    <div className={styles.aiPanelRowActions}>
                      <button
                        type="button"
                        className={styles.aiPanelRowAccept}
                        onClick={() => acceptOne(c.key)}
                        aria-label={t('kb.aiVariantsRowAccept')}
                        title={t('kb.aiVariantsRowAccept')}
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className={styles.aiPanelRowEdit}
                        onClick={() => toggleEdit(c.key)}
                        aria-label={t('kb.aiVariantsRowEdit')}
                        title={t('kb.aiVariantsRowEdit')}
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className={styles.aiPanelRowReject}
                        onClick={() => rejectOne(c.key)}
                        aria-label={t('kb.aiVariantsRowReject')}
                        title={t('kb.aiVariantsRowReject')}
                      >
                        ✕
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
          <div className={styles.aiPanelFooter}>
            <button
              type="button"
              className={styles.aiPanelSuggestMore}
              onClick={() => {
                void fetchBatch();
              }}
            >
              ↻ {t('kb.aiVariantsSuggestMore')}
            </button>
            <span className={styles.aiPanelFooterHelper}>
              {t('kb.aiVariantsSuggestMoreHelper')}
            </span>
          </div>
        </>
      )}

      {status === 'loaded' && candidates.length === 0 && (
        <div className={styles.aiPanelBodyCenter}>
          <div className={styles.aiPanelEmpty}>{t('kb.aiVariantsEmpty')}</div>
          <button
            type="button"
            className={styles.aiPanelSuggestMore}
            onClick={() => {
              void fetchBatch();
            }}
          >
            ↻ {t('kb.aiVariantsSuggestMore')}
          </button>
        </div>
      )}

      {/* batch index for potential debug / dev tools; not user-visible */}
      <div hidden data-batch-idx={batchIdx} />
    </div>
  );
}
